import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectUserId, selectTeams, selectSportsClubId } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import { TeamControllerService } from '../api';
import { OpenAPI } from '../api/core/OpenAPI';
import { request as __request } from '../api/core/request';
import { toast } from 'react-toastify';
import './PreseasonGamesSummaryReport.css';

function PreseasonGamesSummaryReport() {
  const { hasRole } = useAuth();
  const isSportsClub = hasRole('SPORTS_CLUB');
  const isCoach = hasRole('COACH');
  const sportsClubId = useSelector(selectSportsClubId);
  const reduxTeams = useSelector(selectTeams) || [];

  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    if (isCoach) {
      setTeams(reduxTeams.map(t => ({ teamId: t.id, name: t.teamName || t.name })));
    } else if (isSportsClub && sportsClubId) {
      TeamControllerService.getTeamsBySportsClubId(sportsClubId)
        .then(data => setTeams(data || []))
        .catch(() => toast.error('Nie udało się pobrać listy drużyn'));
    }
  }, [isCoach, isSportsClub, sportsClubId, reduxTeams]);

  // Auto-fill date filters from current/nearest season definition
  useEffect(() => {
    __request(OpenAPI, {
      method: 'GET',
      url: '/preseason-times/current-or-nearest',
    }).then(season => {
      if (season) {
        if (season.minStartDate) setDateFrom(season.minStartDate);
        if (season.maxEndDate) setDateTo(season.maxEndDate);
      }
    }).catch(() => { /* ignore - user can fill dates manually */ });
  }, []);

  const handleGenerate = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Wybierz zakres dat');
      return;
    }
    setLoading(true);
    setGenerated(false);
    try {
      const teamId = selectedTeamId ? Number(selectedTeamId) : undefined;
      const data = await __request(OpenAPI, {
        method: 'GET',
        url: '/preseason-games/summary',
        query: {
          'teamId': teamId,
          'dateFrom': dateFrom || undefined,
          'dateTo': dateTo || undefined,
        },
      });
      setGames(data || []);
      setGenerated(true);
      if (!data || data.length === 0) {
        toast.info('Brak potwierdzonych sparingów w wybranym zakresie dat');
      }
    } catch (error) {
      toast.error('Nie udało się pobrać danych');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return 'Nie ustalono';
    return new Date(d).toLocaleDateString('pl-PL');
  };

  const formatTime = (t) => {
    if (!t) return null;
    return typeof t === 'string' ? t.substring(0, 5) : t;
  };

  // Grupuj gry po nazwie drużyny użytkownika
  const groupGamesByTeam = (games) => {
    const groups = {};
    const myTeamIds = new Set(teams.map(t => t.teamId));
    games.forEach(game => {
      // Ustal czy "nasza" drużyna jest creatorem czy opponentem
      const creatorIsMine = myTeamIds.has(game.teamCreatorId);
      const opponentIsMine = myTeamIds.has(game.teamOpponentId);
      // Jeśli obie drużyny są nasze (wewnętrzny sparing), traktuj creatora jako "naszego"
      const isCreator = creatorIsMine && !opponentIsMine ? true
                      : !creatorIsMine && opponentIsMine ? false
                      : creatorIsMine; // fallback: obie nasze → creator
      const yourTeamName = isCreator ? game.teamCreatorName : game.teamOpponentName;
      const key = yourTeamName || 'Nieznana drużyna';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push({ ...game, isCreator });
    });
    return groups;
  };

  const grouped = generated && games.length > 0 ? groupGamesByTeam(games) : {};

  return (
    <div className="summary-report-container">
      <h2 className="summary-report-title">Podsumowanie</h2>

      <div className="summary-report-filters">
        <div className="summary-report-filter-group">
          <label>Drużyna</label>
          <select
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
            className="form-select"
          >
            <option value="">Wszystkie drużyny</option>
            {teams.map(team => (
              <option key={team.teamId} value={team.teamId}>{team.name}</option>
            ))}
          </select>
        </div>

        <div className="summary-report-filter-group">
          <label>Data od</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="summary-report-filter-group">
          <label>Data do</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="summary-report-filter-group summary-report-btn-group">
          <button
            className="summary-report-btn"
            onClick={handleGenerate}
            disabled={loading || !dateFrom || !dateTo}
          >
            {loading ? 'Generowanie...' : 'Generuj'}
          </button>
        </div>
      </div>

      {generated && games.length > 0 && (
        <div className="summary-report-results">
          {Object.entries(grouped).map(([teamName, teamGames]) => (
            <div key={teamName} className="report-team-group">
              <div className="report-team-header">
                <span className="report-team-name">{teamName}</span>
                <span className="report-team-count">{teamGames.length} {teamGames.length === 1 ? 'sparing' : teamGames.length < 5 ? 'sparingi' : 'sparingów'}</span>
              </div>
              <div className="report-games-list">
                {teamGames.map((game, index) => {
                  const opponent = game.isCreator
                    ? (game.isManualOpponent ? game.manualOpponentName : game.teamOpponentName)
                    : game.teamCreatorName;
                  const opponentLogo = game.isCreator ? game.opponentLogoUrl : game.creatorLogoUrl;

                  return (
                    <div key={game.id} className="report-game-card">
                      <div className="report-game-index">{index + 1}</div>
                      <div className="report-game-logo-wrapper">
                        {opponentLogo ? (
                          <img src={opponentLogo} alt="" className="report-game-logo" />
                        ) : (
                          <div className="report-game-logo-placeholder" />
                        )}
                      </div>
                      <div className="report-game-info">
                        <div className="report-game-opponent">{opponent || 'Nieznany'}</div>
                        <div className="report-game-details">
                          <span className="report-game-date">{game.date ? formatDate(game.date) : 'do ustalenia'}</span>
                          <span className="report-game-time">{game.startTime && game.endTime ? `${formatTime(game.startTime)} - ${formatTime(game.endTime)}` : 'do ustalenia'}</span>
                          <span className="report-game-location">{game.location || '-'}</span>
                        </div>
                      </div>
                      {game.scoreCreator != null && game.scoreOpponent != null && (() => {
                        const myScore = game.isCreator ? game.scoreCreator : game.scoreOpponent;
                        const theirScore = game.isCreator ? game.scoreOpponent : game.scoreCreator;
                        const resultLabel = myScore > theirScore ? 'Zwycięstwo' : myScore < theirScore ? 'Porażka' : 'Remis';
                        const resultClass = myScore > theirScore ? 'score-win' : myScore < theirScore ? 'score-loss' : 'score-draw';
                        return (
                          <div className="report-game-score">
                            <span className={`report-score-badge ${resultClass}`}>{resultLabel}</span>
                            <span className="report-score-value">{myScore} : {theirScore}</span>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {generated && games.length === 0 && (
        <div className="summary-report-empty">
          Brak potwierdzonych sparingów w wybranym zakresie dat.
        </div>
      )}
    </div>
  );
}

export default PreseasonGamesSummaryReport;
